import React, { useEffect, useMemo, useState } from "react";
import Draggable from "react-draggable";
import {
  buildLocationVersionEntryFromDevice,
  formatTime,
} from "../../functions/mdmDeviceDisplayUtils";
import MdmLocationVersionMapPanel from "./DeviceInfoModal_components/MdmLocationVersionMapPanel";

const formatCoordinate = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  return String(value);
};

const LocationSourceToggle = ({ locationSource, onLocationSourceChange }) => (
  <div className="inline-flex shrink-0 rounded-lg bg-surface-container-low p-0.5">
    <button
      type="button"
      className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
        locationSource === "mdm"
          ? "bg-primary text-white"
          : "text-on-surface-variant hover:bg-surface-container-high"
      }`}
      onClick={() => onLocationSourceChange("mdm")}
    >
      MDM
    </button>
    <button
      type="button"
      className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
        locationSource === "logs"
          ? "bg-primary text-white"
          : "text-on-surface-variant hover:bg-surface-container-high"
      }`}
      onClick={() => onLocationSourceChange("logs")}
    >
      Logs
    </button>
  </div>
);

const MdmLocationVersionWindow = ({
  isOpen,
  onClose,
  branchLabel,
  retailerId,
  branchId,
  deviceRows = [],
  deviceActionResults = {},
  onRefreshMdm,
  isRefreshingMdm = false,
  mdmRefreshError = "",
  onRefreshLogs,
  isRefreshingLogs = false,
  logsRefreshError = "",
  storeCoordinates,
  branchMapData,
  isBranchMapDataLoading,
  branchMapDataError,
}) => {
  const [locationSource, setLocationSource] = useState("mdm");
  const [focusedDeviceId, setFocusedDeviceId] = useState(null);
  const [isDeviceListExpanded, setIsDeviceListExpanded] = useState(true);

  const deviceEntries = useMemo(
    () =>
      deviceRows.map((device) => {
        const deviceId = String(device?.deviceId || "").trim();
        return buildLocationVersionEntryFromDevice(device, locationSource, {
          locationLogResult: deviceActionResults?.[deviceId]?.fetch_location_log ?? null,
        });
      }),
    [deviceActionResults, deviceRows, locationSource]
  );

  const devicesWithDataCount = useMemo(
    () => deviceEntries.filter((entry) => entry.hasData).length,
    [deviceEntries]
  );

  const devicesWithCoordinatesCount = useMemo(
    () =>
      deviceEntries.filter(
        (entry) =>
          entry.latitude != null &&
          entry.latitude !== "" &&
          entry.longitude != null &&
          entry.longitude !== ""
      ).length,
    [deviceEntries]
  );

  useEffect(() => {
    if (!isOpen) {
      setFocusedDeviceId(null);
      setLocationSource("mdm");
    }
  }, [isOpen]);

  useEffect(() => {
    if (!focusedDeviceId) return;
    const focusedEntry = deviceEntries.find((entry) => entry.deviceId === focusedDeviceId);
    if (!focusedEntry) {
      setFocusedDeviceId(null);
    }
  }, [deviceEntries, focusedDeviceId]);

  if (!isOpen) return null;

  const sourceLabel = locationSource === "mdm" ? "MDM device info" : "location logs";

  return (
    <div className="fixed inset-0 z-[110] pointer-events-none">
      <Draggable
        handle=".mdm-location-version-window-handle"
        cancel=".mdm-location-version-window-cancel"
        bounds="parent"
      >
        <div className="pointer-events-auto absolute top-4 left-4 w-[calc(100vw-2rem)] max-w-[1280px] h-[calc(100vh-2rem)] bg-surface-container-lowest rounded-2xl shadow-2xl border border-outline-variant/20 overflow-hidden flex flex-col">
          <div className="mdm-location-version-window-handle cursor-move px-5 py-3 border-b border-outline-variant/10 flex items-center justify-between gap-3 bg-surface-container-low/40 shrink-0">
            <div className="min-w-0 flex items-center gap-3">
              <div className="p-1.5 bg-primary-fixed rounded-lg text-primary shrink-0">
                <span className="material-symbols-outlined text-base">location_on</span>
              </div>
              <div className="min-w-0">
                <h2 className="font-headline font-bold text-base text-on-surface">
                  Location / Version
                </h2>
                <p className="text-[11px] text-on-surface-variant truncate">
                  {branchLabel ? `${branchLabel} · ` : ""}
                  {deviceEntries.length} device(s) · {devicesWithDataCount} with {sourceLabel} ·{" "}
                  {devicesWithCoordinatesCount} on map
                </p>
              </div>
            </div>
            <button
              type="button"
              className="mdm-location-version-window-cancel p-2 hover:bg-surface-container-low rounded-full transition-colors text-on-surface-variant shrink-0"
              onClick={onClose}
              aria-label="Close location/version window"
            >
              <span className="material-symbols-outlined">close</span>
            </button>
          </div>

          <div className="mdm-location-version-window-cancel shrink-0 px-4 py-3 border-b border-outline-variant/10 bg-surface-container-high">
            <div className="flex items-center gap-3 overflow-x-auto flex-wrap">
              <LocationSourceToggle
                locationSource={locationSource}
                onLocationSourceChange={setLocationSource}
              />
              {locationSource === "mdm" && (
                <button
                  type="button"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-surface-container-low text-on-surface text-xs font-semibold hover:bg-surface-container-highest transition-colors disabled:opacity-60 disabled:cursor-not-allowed shrink-0"
                  onClick={() => onRefreshMdm?.()}
                  disabled={isRefreshingMdm || deviceRows.length === 0}
                >
                  <span className="material-symbols-outlined text-sm">refresh</span>
                  {isRefreshingMdm ? "Refreshing..." : "Refresh"}
                </button>
              )}
              {locationSource === "logs" && (
                <button
                  type="button"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-surface-container-low text-on-surface text-xs font-semibold hover:bg-surface-container-highest transition-colors disabled:opacity-60 disabled:cursor-not-allowed shrink-0"
                  onClick={() => onRefreshLogs?.()}
                  disabled={isRefreshingLogs || deviceRows.length === 0}
                >
                  <span className="material-symbols-outlined text-sm">refresh</span>
                  {isRefreshingLogs ? "Refreshing..." : "Refresh"}
                </button>
              )}
              <span className="text-xs text-on-surface-variant">
                {locationSource === "mdm"
                  ? "Showing location from cached MDM device info."
                  : "Showing location and app version from cached location logs."}
              </span>
            </div>
            {locationSource === "mdm" && mdmRefreshError && (
              <p className="text-xs text-error mt-2">{mdmRefreshError}</p>
            )}
            {locationSource === "logs" && logsRefreshError && (
              <p className="text-xs text-error mt-2">{logsRefreshError}</p>
            )}
          </div>

          <div className="mdm-location-version-window-cancel flex-1 min-h-0 flex flex-col">
            <MdmLocationVersionMapPanel
              deviceEntries={deviceEntries}
              focusedDeviceId={focusedDeviceId}
              locationSource={locationSource}
              retailerId={retailerId}
              branchId={branchId}
              storeCoordinates={storeCoordinates}
              branchMapData={branchMapData}
              isBranchMapDataLoading={isBranchMapDataLoading}
              branchMapDataError={branchMapDataError}
              isMapActive={isOpen}
              fillHeight
            />

            <div className="shrink-0 border-t border-outline-variant/10 bg-surface-container-high">
              <button
                type="button"
                className="w-full px-4 py-2 flex items-center justify-between text-xs font-semibold text-on-surface hover:bg-surface-container-low/60 transition-colors"
                onClick={() => setIsDeviceListExpanded((prev) => !prev)}
              >
                <span className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-sm">devices</span>
                  Selected Devices ({deviceEntries.length})
                </span>
                <span className="material-symbols-outlined text-sm">
                  {isDeviceListExpanded ? "expand_more" : "expand_less"}
                </span>
              </button>

              {isDeviceListExpanded && (
                <div className="max-h-[200px] overflow-auto border-t border-outline-variant/10">
                  {deviceEntries.length === 0 ? (
                    <p className="px-4 py-4 text-xs text-on-surface-variant text-center">
                      No devices selected.
                    </p>
                  ) : (
                    <table className="min-w-full text-left text-xs">
                      <thead className="bg-surface-container-low/50 sticky top-0 z-[1]">
                        <tr className="text-[10px] text-on-surface-variant uppercase tracking-wide">
                          <th className="px-3 py-2 font-bold whitespace-nowrap">Device ID</th>
                          <th className="px-3 py-2 font-bold whitespace-nowrap">App Version</th>
                          <th className="px-3 py-2 font-bold whitespace-nowrap">
                            {locationSource === "mdm" ? "Agent Check-in" : "Updated"}
                          </th>
                          <th className="px-3 py-2 font-bold whitespace-nowrap">Latitude</th>
                          <th className="px-3 py-2 font-bold whitespace-nowrap">Longitude</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-outline-variant/20">
                        {deviceEntries.map((entry) => {
                          const isFocused = focusedDeviceId === entry.deviceId;
                          return (
                            <tr
                              key={`${entry.deviceId}-${locationSource}`}
                              className={`cursor-pointer transition-colors ${
                                isFocused
                                  ? "bg-primary-fixed/40"
                                  : "hover:bg-surface-container-low/60"
                              }`}
                              onClick={() =>
                                setFocusedDeviceId((prev) =>
                                  prev === entry.deviceId ? null : entry.deviceId
                                )
                              }
                            >
                              <td className="px-3 py-2 font-mono text-[11px] text-on-surface whitespace-nowrap">
                                {entry.deviceId}
                              </td>
                              <td className="px-3 py-2 text-on-surface-variant whitespace-nowrap">
                                {entry.appVersion != null && entry.appVersion !== ""
                                  ? String(entry.appVersion)
                                  : "No Data"}
                              </td>
                              <td className="px-3 py-2 text-on-surface-variant whitespace-nowrap">
                                {entry.updatedAt ? formatTime(entry.updatedAt) : "No Data"}
                              </td>
                              <td className="px-3 py-2 font-mono text-[11px] text-on-surface-variant whitespace-nowrap">
                                {formatCoordinate(entry.latitude)}
                              </td>
                              <td className="px-3 py-2 font-mono text-[11px] text-on-surface-variant whitespace-nowrap">
                                {formatCoordinate(entry.longitude)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                  <p className="px-4 py-2 text-[10px] text-on-surface-variant border-t border-outline-variant/10">
                    Click a row to focus its marker. Click again to return to the branch map view.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </Draggable>
    </div>
  );
};

export default MdmLocationVersionWindow;
